/**
 * 심볼 관련 유틸리티 함수들
 * 가격 계산, 기준값 관리, 증감률 계산 등을 담당
 */
import { SYMBOL_LIST_TAB, ALL_SYMBOLS, } from '@template/types';
/**
 * 가격을 적절한 소수점 자릿수로 포맷팅
 * @param price - 포맷팅할 가격
 * @returns 포맷팅된 가격 문자열
 */
export const formatPrice = (price) => {
    if (price < 1) {
        return price.toFixed(4);
    }
    else if (price < 100) {
        return price.toFixed(2);
    }
    else {
        return price.toFixed(0);
    }
};
/**
 * 실시간 가격 정보 가져오기 (NaN 방지)
 * @param ticker - 심볼 티커
 * @param marketData - 시장 데이터 배열
 * @returns 포맷팅된 가격 문자열 또는 '-'
 */
export const getSymbolPrice = (ticker, marketData) => {
    const marketDataForSymbol = marketData.find((m) => m.symbol === ticker);
    if (!marketDataForSymbol)
        return '-';
    const price = marketDataForSymbol.sellPrice;
    // 유효한 가격인지 검증 (숫자이고 NaN이 아니며 양수)
    if (typeof price !== 'number' || isNaN(price) || price <= 0) {
        return '-';
    }
    return formatPrice(price);
};
/**
 * 심볼 필터링 (탭별, 검색어별)
 * @param symbols - 전체 심볼 배열
 * @param activeTab - 활성 탭
 * @param searchQuery - 검색어
 * @param favorites - 관심 종목 Set
 * @returns 필터링된 심볼 배열
 */
export const filterSymbols = (symbols, activeTab, searchQuery, favorites) => {
    let filtered = symbols;
    // 탭별 필터링
    if (activeTab === SYMBOL_LIST_TAB.WATCHLIST) {
        filtered = filtered.filter((symbol) => favorites.has(symbol.ticker));
    }
    else if (activeTab === SYMBOL_LIST_TAB.POSSESSION) {
        // FIXME: 보유 종목 데이터 연동 필요
        // 현재는 암호화폐만 보유 종목으로 표시
        filtered = filtered.filter((symbol) => symbol.type === 'crypto');
    }
    // 검색 필터링
    if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter((symbol) => symbol.ticker.toLowerCase().includes(query) ||
            symbol.description?.toLowerCase().includes(query));
    }
    return filtered;
};
/**
 * 실제 사용 가능한 종목 코드 목록에서 첫 번째 종목 코드를 반환
 * @param availableSymbolCodes - 사용 가능한 종목 코드 배열
 * @returns 첫 번째 종목 코드 또는 빈 문자열
 */
export function getFirstAvailableSymbolCode(availableSymbolCodes) {
    return availableSymbolCodes.length > 0 ? availableSymbolCodes[0] : '';
}
/**
 * 실제 사용 가능한 종목 코드 목록에서 첫 번째 종목을 TradingSymbol로 반환
 * @param availableSymbolCodes - 사용 가능한 종목 코드 배열
 * @returns 첫 번째 TradingSymbol 또는 null
 */
export function getFirstAvailableSymbol(availableSymbolCodes) {
    if (availableSymbolCodes.length === 0) {
        return null;
    }
    const firstCode = availableSymbolCodes[0];
    const symbol = ALL_SYMBOLS.find((s) => s.ticker === firstCode);
    return symbol || null;
}
/**
 * 실제 사용 가능한 종목 코드 목록에서 필요한 개수만큼 TradingSymbol 배열로 반환
 * @param availableSymbolCodes - 사용 가능한 종목 코드 배열
 * @param count - 필요한 종목 개수
 * @returns TradingSymbol 배열
 */
export function getAvailableSymbols(availableSymbolCodes, count) {
    const codes = availableSymbolCodes.slice(0, count);
    return codes
        .map((code) => ALL_SYMBOLS.find((s) => s.ticker === code))
        .filter((s) => s !== undefined);
}
/**
 * 종목 그룹 코드를 TradingView 차트 타입으로 변환
 * @param stockGroupCd - 종목 그룹 코드 (01: forex, 02: index, 03: commodity, 04: crypto)
 * @returns TradingView symbol type
 */
export function getChartTypeFromStockGroupCd(stockGroupCd) {
    switch (stockGroupCd) {
        case '01':
            return 'forex';
        case '02':
            return 'index';
        case '03':
            return 'commodity';
        case '04':
            return 'crypto';
        default:
            return 'forex';
    }
}
/**
 * 종목 코드와 메타데이터를 TradingSymbol로 변환
 * @param stockCd - 종목 코드
 * @param metadata - 종목 메타데이터 (stockGroupCd 포함). null/undefined이면 null 반환
 * @returns TradingSymbol 또는 null
 */
export function stockCdToTradingSymbol(stockCd, metadata) {
    if (metadata == null) {
        return null;
    }
    return {
        symbol: stockCd,
        ticker: stockCd,
        type: getChartTypeFromStockGroupCd(metadata.stockGroupCd),
    };
}
//# sourceMappingURL=symbol.js.map